// 导入原始routes配置
import originalRoutes from '../../config/routes';
// 静态路由树配置，包含is_show属性来控制菜单显示隐藏
export interface RouteItem {
  path: string;
  name?: string;
  icon?: string;
  component?: string;
  layout?: boolean;
  access?: string;
  redirect?: string;
  routes?: RouteItem[];
  type?: number; // 1=菜单, 2=页面, 3=按钮
  is_show: boolean;
}

// 将原始routes配置转换为带is_show属性的静态路由树
export const staticRouteMap: RouteItem[] = (() => {
  // 递归转换路由配置，添加is_show属性
  const transformRoutes = (routes: any[]): RouteItem[] => {
    return routes.map(route => {
      const transformedRoute: RouteItem = {
        path: route.path,
        name: route.name,
        icon: route.icon,
        component: route.component,
        layout: route.layout !== undefined ? route.layout : true,
        access: route.access,
        redirect: route.redirect,
        is_show: route.is_show !== undefined ? route.is_show : false, // 默认显示
      };

      if (route.routes && route.routes.length > 0) {
        transformedRoute.routes = transformRoutes(route.routes);
      }

      return transformedRoute;
    });
  };

  return transformRoutes(originalRoutes);
})();

// 过滤路由树的函数，根据is_show属性过滤掉不应显示的菜单
export const filterRoutesByShow = (routes: RouteItem[]): RouteItem[] => {
  return routes
    .filter(route => route.is_show && route.type !== 3) // 排除按钮类型(type=3)的菜单项
    .map(route => {
      if (route.routes && route.routes.length > 0) {
        const filteredRoutes = filterRoutesByShow(route.routes);
        return {
          ...route,
          routes: filteredRoutes,
        };
      }
      return route;
    })
    .filter(Boolean) as RouteItem[];
};

import { getCacheUserPermission } from '@/services/ant-design-pro/api';

/**
 * 在路由树中递归查找是否存在指定条件的权限节点
 * @param routes 路由树
 * @param predicate 匹配条件
 */
export const hasPermission = (routes: RouteItem[], predicate: (route: RouteItem) => boolean): boolean => {
  for (const route of routes) {
    if (predicate(route)) return true;
    if (route.routes?.length && hasPermission(route.routes, predicate)) return true;
  }
  return false;
};

/**
 * 统一 is_show 字段判定（兼容历史 number 和 boolean 两种类型）
 */
const isRouteShown = (route: RouteItem): boolean => {
  return route.is_show === true || route.is_show === 1;
};

/**
 * 将动态子路由合并到已有的子路由列表中
 */
const mergeChildrenWithDynamic = (
  existingChildren: RouteItem[],
  parentPath: string,
  dynamicChildMap: Map<string, RouteItem[]>,
  processed: Set<string>,
  mergeChildArrays: (routes: RouteItem[], parentPath: string) => RouteItem[],
): RouteItem[] => {
  const children = [...existingChildren];
  const dynamicChildren = dynamicChildMap.get(parentPath);
  if (!dynamicChildren) return children;

  dynamicChildren.forEach((child) => {
    if (!isRouteShown(child)) return;
    if (processed.has(child.path)) return;
    processed.add(child.path);
    const newChild = { ...child };
    if (child.routes) {
      newChild.routes = mergeChildArrays(child.routes, child.path);
    }
    children.push(newChild);
  });
  return children;
};

// 合并静态路由和动态路由
const mergeRoutes = (staticRoutes: RouteItem[], dynamicRoutes: RouteItem[]): RouteItem[] => {
  // 创建动态路由的路径映射，便于查找
  const dynamicRoutePathMap = new Map<string, RouteItem>();
  // 创建父路径到子路由的映射，用于维护路由层级结构
  const parentPathToRoutesMap = new Map<string, RouteItem[]>();

  // 递归将动态路由添加到映射中，并构建父子关系映射
  const addRoutesToMap = (routes: RouteItem[], parentPath = '') => {
    routes.forEach(route => {
      dynamicRoutePathMap.set(route.path, route);

      // 构建父子关系映射
      if (parentPath) {
        if (!parentPathToRoutesMap.has(parentPath)) {
          parentPathToRoutesMap.set(parentPath, []);
        }
        parentPathToRoutesMap.get(parentPath)!.push(route);
      }

      if (route.routes) {
        addRoutesToMap(route.routes, route.path);
      }
    });
  };

  // 确保动态路由是有效的数组
  if (Array.isArray(dynamicRoutes)) {
    addRoutesToMap(dynamicRoutes);
  } else {
    console.error('动态路由不是有效的数组:', dynamicRoutes);
    return staticRoutes;
  }
  // 递归合并路由
  const processedPaths = new Set<string>();
  const mergeRouteArrays = (staticRouteArray: RouteItem[], parentPath = ''): RouteItem[] => {
    const result: RouteItem[] = [];

    staticRouteArray.forEach(route => {
      if (processedPaths.has(route.path)) return;
      processedPaths.add(route.path);

      const dynamicRoute = dynamicRoutePathMap.get(route.path);

      if (dynamicRoute) {
        // 动态路由 is_show 为 false 时不合并
        if (!isRouteShown(dynamicRoute)) return;

        const mergedRoute: RouteItem = {
          ...route,
          name: dynamicRoute.name || route.name,
          icon: dynamicRoute.icon || route.icon,
          access: dynamicRoute.access || route.access,
          redirect: dynamicRoute.redirect || route.redirect,
          is_show: dynamicRoute.is_show !== undefined ? dynamicRoute.is_show : true,
          component: route.component || dynamicRoute.component,
        };

        // 统一处理子路由：先合并静态子路由，再合并动态子路由
        const staticChildren = route.routes
          ? mergeRouteArrays(route.routes, route.path)
          : [];
        const allChildren = mergeChildrenWithDynamic(
          staticChildren, route.path, parentPathToRoutesMap, processedPaths, mergeRouteArrays,
        );
        if (allChildren.length > 0) mergedRoute.routes = allChildren;

        result.push(mergedRoute);
        return;
      }

      // 无匹配动态路由，保留静态路由并递归处理子路由
      const preservedRoute = { ...route };
      if (route.routes) {
        preservedRoute.routes = mergeRouteArrays(route.routes, route.path);
      }
      const allChildren = mergeChildrenWithDynamic(
        preservedRoute.routes || [], route.path, parentPathToRoutesMap, processedPaths, mergeRouteArrays,
      );
      if (allChildren.length > 0) preservedRoute.routes = allChildren;

      result.push(preservedRoute);
    });

    // 添加孤立的顶级动态路由
    if (!parentPath) {
      dynamicRoutes.forEach((route) => {
        const hasParent = [...parentPathToRoutesMap.values()]
          .some(children => children.some(child => child.path === route.path));
        if (!processedPaths.has(route.path) && !hasParent && isRouteShown(route)) {
          processedPaths.add(route.path);
          const orphanRoute = { ...route };
          if (route.routes) {
            orphanRoute.routes = mergeRouteArrays(route.routes, route.path);
          }
          result.push(orphanRoute);
        }
      });
    }

    return result;
  };

  const mergedRoutes = mergeRouteArrays(staticRoutes);
  return mergedRoutes;
};

// 从接口获取路由配置的函数
export const fetchRoutesFromApi = async (): Promise<RouteItem[]> => {
  try {
    // 调用getCacheUserPermission接口获取菜单数据
    const response = await getCacheUserPermission();
    // 检查响应是否成功
    if (response && response.code === 0 && response.data) {
      // 假设接口返回的数据结构中，菜单列表在data.routes或data.list字段
      const apiRoutes = response.data;
      // 转换API返回的数据为前端可用的路由格式
      const transformedRoutes = transformApiRoutesToRouteMap(apiRoutes);
      const mergedRoutes = mergeRoutes(staticRouteMap, transformedRoutes);
      return mergedRoutes;
    } else {
      console.warn('API返回数据格式不符合预期:', response);
      // 返回静态路由作为备用
      return staticRouteMap;
    }
  } catch (error) {
    console.error('Failed to fetch routes from API:', error);
    return staticRouteMap; // 失败时返回静态路由
  }
};

/**
 * 更新菜单的显示状态
 * @param menuPath 菜单路径
 * @param isShow 是否显示
 * @returns Promise<boolean>
 * @deprecated 当前为桩实现，仅在前端维护状态，需对接后端接口以持久化
 */
export const updateMenuShowStatus = async (menuPath: string, isShow: boolean): Promise<boolean> => {
  try {
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * 批量更新菜单的显示状态
 * @param menuStatusList 菜单状态列表
 * @returns Promise<boolean>
 * @deprecated 当前为桩实现，仅在前端维护状态，需对接后端批量更新接口
 */
export const batchUpdateMenuShowStatus = async (menuStatusList: Array<{menuPath: string; isShow: boolean}>): Promise<boolean> => {
  try {
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * 根据路径查找路由项
 * @param routes 路由列表
 * @param path 要查找的路径
 * @returns 找到的路由项或undefined
 */
export const findRouteByPath = (routes: RouteItem[], path: string): RouteItem | undefined => {
  for (const route of routes) {
    if (route.path === path) {
      return route;
    }
    if (route.routes) {
      const found = findRouteByPath(route.routes, path);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

// 用于处理接口返回的菜单数据，转换为前端可用的路由格式
export const transformApiRoutesToRouteMap = (apiRoutes: any[]): RouteItem[] => {
  return apiRoutes.map(apiRoute => {
    const route: RouteItem = {
      path: apiRoute.components_path || '',
      name: apiRoute.name || '',
      icon: apiRoute.icon || '',
      component: apiRoute.component || '',
      layout: apiRoute.layout !== undefined ? apiRoute.layout : true,
      access: apiRoute.access || '',
      redirect: apiRoute.redirect || '',
      type: apiRoute.type,
      is_show: apiRoute.is_show !== undefined ? apiRoute.is_show : true,
    };

    if (apiRoute.children && apiRoute.children.length > 0) {
      route.routes = transformApiRoutesToRouteMap(apiRoute.children);
    }

    return route;
  });
};
